import { useDeviceIdContext } from '@/components/device-id-provider';
import { Button } from '@/components/ui/button';
import { useHeyGenToken } from '@/hooks/useHeyGenToken';
import { useSessionSummary } from '@/hooks/useSessionSummary';
import { cn } from '@/lib/utils';
import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
} from '@heygen/streaming-avatar';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  Mic,
  MicOff,
  PhoneOff,
  Volume2,
  VolumeX,
  Check,
  Loader2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type AvatarCompanyConfig } from './avatar-configs';
import { UOBVisuals } from './UOBVisuals';

// Voice chat configuration
// Note: HeyGen SDK only supports isInputAudioMuted parameter for startVoiceChat
// Interruption prevention is handled by muting user input when avatar speaks

interface UnifiedAvatarCallProps {
  companyConfig: AvatarCompanyConfig;
  mode: 'voice' | 'video';
}

export function UnifiedAvatarCall({ companyConfig, mode }: UnifiedAvatarCallProps) {
  const navigate = useNavigate();
  const { deviceId } = useDeviceIdContext();
  const {
    token: heyGenToken,
    generateToken,
    loading: tokenLoading,
  } = useHeyGenToken();
  const { endSession, summaryData } = useSessionSummary();

  // CX scenario detection and language
  const isCxScenario = !!companyConfig.cxScenarioCode;
  const isZh = companyConfig.language === 'zh';

  // Stages
  const stageNames = companyConfig.stages?.names || ['Introduction', 'Training', 'Quiz', 'Complete'];
  const [currentStage, setCurrentStage] = useState(0);
  const [quizQuestion, setQuizQuestion] = useState(1);
  const [activePrinciple, setActivePrinciple] = useState<string | null>(null);
  const lastDetectedPrincipleRef = useRef<string | null>(null);

  // UOB-specific visual states
  const [exemptMode, setExemptMode] = useState<'exempt-only' | 'comparison' | null>(null);
  const [quizState, setQuizState] = useState<'waiting' | 'answered' | 'revealed'>('waiting');
  const [userQuizAnswer, setUserQuizAnswer] = useState<string | null>(null);

  // States
  const [isInitializing, setIsInitializing] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [userStream, setUserStream] = useState<MediaStream | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const avatarRef = useRef<StreamingAvatar | null>(null);
  const voiceChatReadyRef = useRef(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const avatarVideoElementRef = useRef<HTMLVideoElement | null>(null);
  const isLeavingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const currentStageRef = useRef<number>(0);
  const coachUtteranceRef = useRef('');
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isAudioOnRef = useRef(true);
  const isAvatarSpeakingRef = useRef(false);
  const lastMessageTimeRef = useRef<number>(0);
  const speechEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioBufferTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Buffer after AVATAR_END_MESSAGE
  const userTranscriptionReceivedRef = useRef(false); // Track if user's speech was transcribed
  const currentTaskIdRef = useRef<string | null>(null); // Track current speech task for correlation

  // Auto-start on mount
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      handleStartCall();
    }

    return () => {
      stopKeepAlive();
      if (speechEndTimeoutRef.current) {
        clearTimeout(speechEndTimeoutRef.current);
      }
      if (audioBufferTimeoutRef.current) {
        clearTimeout(audioBufferTimeoutRef.current);
      }
      if (userStream) {
        userStream.getTracks().forEach((track) => track.stop());
      }
      if (avatarRef.current && !isLeavingRef.current) {
        avatarRef.current.stopAvatar().catch(console.error);
      }
      voiceChatReadyRef.current = false;
    };
  }, []);

  useEffect(() => {
    currentStageRef.current = currentStage;
  }, [currentStage]);

  // Keep isAudioOnRef in sync with state
  useEffect(() => {
    isAudioOnRef.current = isAudioOn;
  }, [isAudioOn]);

  // Initialize user media
  const ensureUserMicrophoneActive = (overrideStream?: MediaStream) => {
    const stream = overrideStream ?? userStream;
    console.log('[Avatar:HeyGen] Ensuring user microphone is active');
    if (!stream) {
      console.warn('[Avatar:HeyGen] No user media stream available');
      return;
    }

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) {
      console.warn('[Avatar:HeyGen] No audio track found in user media stream');
      return;
    }

    if (!audioTrack.enabled) {
      audioTrack.enabled = true;
      console.log('[Avatar:HeyGen] ✅ Enabled user audio track');
    }

    if (!isAudioOn) {
      setIsAudioOn(true);
    }

    if (avatarRef.current) {
      try {
        avatarRef.current.unmuteInputAudio();
        console.log('[Avatar:HeyGen] 🎤 HeyGen input unmuted');
      } catch (error) {
        console.warn('[Avatar:HeyGen] Failed to unmute input audio:', error);
      }
    }
  };

  const advanceStage = (nextStage: number, reason: string) => {
    setCurrentStage((prev) => {
      if (prev >= nextStage) {
        return prev;
      }

      console.log('[Avatar:Stage] 🎯 TRANSITION', {
        from: stageNames[prev],
        to: stageNames[nextStage],
        reason,
        timestamp: new Date().toISOString(),
      });

      currentStageRef.current = nextStage;
      return nextStage;
    });
  };

  const initializeUserMedia = async () => {
    try {
      // Only request audio - no webcam needed
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });
      setUserStream(stream);
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        console.log('[Avatar:HeyGen] User microphone ready:', {
          label: audioTrack.label,
          enabled: audioTrack.enabled,
          settings: audioTrack.getSettings(),
        });
      }
      ensureUserMicrophoneActive(stream);
      return stream;
    } catch (error) {
      console.error('Failed to access media devices:', error);
      setError(
        'Unable to access microphone. Please grant permissions.',
      );
      throw error;
    }
  };

  // Start voice chat for two-way conversation
  // Start voice chat - AI will speak first based on system prompt instructions
  // Auto-muting happens via event handlers when avatar speaks
  const startVoiceChat = async () => {
    if (!avatarRef.current) {
      console.error('[Avatar:HeyGen] Cannot start voice chat - no avatar ref');
      return;
    }

    if (voiceChatReadyRef.current) {
      console.log('[Avatar:HeyGen] Voice chat already active');
      return;
    }

    try {
      console.log('[Avatar:HeyGen] 🎤 Starting voice chat...');

      await (avatarRef.current as any).startVoiceChat({
        isInputAudioMuted: false, // Start unmuted - muting handled by event handlers
      });

      voiceChatReadyRef.current = true;
      setIsListening(true);
      console.log('[Avatar:HeyGen] ✅ Voice chat ready - AI will speak first based on system prompt');

      // Start keepalive to prevent idle disconnection
      startKeepAlive();

    } catch (error) {
      console.error('[Avatar:HeyGen] ❌ Failed to start voice chat:', error);
      throw error;
    }
  };

  // Keepalive mechanism to prevent idle disconnection
  const startKeepAlive = () => {
    // Clear any existing interval
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
    }

    console.log('[Avatar:HeyGen] 🔄 Starting keepalive mechanism');

    // Send keepalive every 30 seconds to prevent idle timeout
    // Also check for stuck mic state every 5 seconds
    keepAliveIntervalRef.current = setInterval(() => {
      if (!avatarRef.current || isLeavingRef.current) {
        console.log('[Avatar:HeyGen] Stopping keepalive - avatar not active');
        stopKeepAlive();
        return;
      }

      const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;

      // SAFETY CHECK: If mic is muted but avatar hasn't spoken in 3+ seconds, unmute
      // This catches edge cases where STOP_TALKING event was missed
      if (!isAvatarSpeakingRef.current && timeSinceLastMessage > 3000 && voiceChatReadyRef.current && isAudioOnRef.current) {
        // Check if user mic is currently muted by trying to detect state
        // If we're in listening state but mic seems stuck, force unmute
        try {
          const inputMuted = avatarRef.current?.isInputAudioMuted;
          if (inputMuted) {
            debugLog('🔧 SAFETY: Detected stuck muted mic - forcing unmute', {
              timeSinceLastMessage: Math.round(timeSinceLastMessage / 1000) + 's',
              isProcessing,
              isListening,
            });
            avatarRef.current.unmuteInputAudio();
          }
        } catch (e) {
          // Ignore errors checking mute state
        }
      }

      // IMPORTANT: Don't interfere when avatar is speaking
      if (isAvatarSpeakingRef.current) {
        console.log('[Avatar:HeyGen] 💓 Keepalive skipped - avatar speaking');
        return;
      }

      const timeSinceActivity = Date.now() - lastActivityRef.current;
      console.log('[Avatar:HeyGen] 💓 Keepalive ping', {
        timeSinceActivity: Math.round(timeSinceActivity / 1000) + 's',
        voiceChatReady: voiceChatReadyRef.current
      });
    }, 5000); // Check every 5 seconds for stuck state, keepalive handled by SDK
  };

  const stopKeepAlive = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
      console.log('[Avatar:HeyGen] 🛑 Keepalive stopped');
    }
  };

  // Update activity timestamp when events occur
  const updateActivity = () => {
    lastActivityRef.current = Date.now();
  };

  // DEBUG: Log helper with timestamp
  const debugLog = (event: string, data?: any) => {
    const now = Date.now();
    const elapsed = sessionStartTime > 0 ? `+${((now - sessionStartTime) / 1000).toFixed(2)}s` : '0s';
    console.log(`[Avatar:DEBUG] [${elapsed}] ${event}`, data || '');
  };

  // Helper to mute USER's microphone input (NOT avatar's audio output)
  const muteUserMic = (reason: string) => {
    if (!avatarRef.current || !voiceChatReadyRef.current) return;
    try {
      avatarRef.current.muteInputAudio();
      debugLog(`🔇 USER MIC MUTED (${reason})`, {
        avatarAudioMuted: avatarVideoElementRef.current?.muted
      });
    } catch (e) {
      console.warn('[Avatar:HeyGen] Failed to mute user mic:', e);
    }
  };

  // Helper to unmute USER's microphone input (NOT avatar's audio output)
  const unmuteUserMic = (reason: string) => {
    if (!avatarRef.current || !voiceChatReadyRef.current || !isAudioOnRef.current) return;
    try {
      avatarRef.current.unmuteInputAudio();
      debugLog(`🎤 USER MIC UNMUTED (${reason})`, {
        avatarAudioMuted: avatarVideoElementRef.current?.muted
      });
      // SAFEGUARD: Ensure avatar audio is NOT muted
      if (avatarVideoElementRef.current && avatarVideoElementRef.current.muted && isSpeakerOn) {
        avatarVideoElementRef.current.muted = false;
        debugLog('⚠️ Fixed: Avatar audio was accidentally muted, restored');
      }
    } catch (e) {
      console.warn('[Avatar:HeyGen] Failed to unmute user mic:', e);
    }
  };

  // Handle speech end - unmute user input when avatar finishes speaking
  const handleSpeechEnd = (reason: string, taskId?: string) => {
    // NOTE: We no longer check for task mismatch because:
    // - Task B can start before Task A's STOP_TALKING fires
    // - If we ignore Task A's STOP_TALKING, mic stays muted forever
    // - Any STOP_TALKING when isSpeaking=true should unmute
    if (taskId && currentTaskIdRef.current && taskId !== currentTaskIdRef.current) {
      debugLog(`ℹ️ Speech end from different task (${reason}) - still processing`, {
        eventTaskId: taskId,
        currentTaskId: currentTaskIdRef.current,
      });
      // Don't return - still process this event to ensure mic gets unmuted
    }

    const wasSpeaking = isAvatarSpeakingRef.current;

    debugLog(`🔇 handleSpeechEnd (${reason})`, {
      wasSpeaking,
      taskId,
      currentTask: currentTaskIdRef.current,
    });

    if (wasSpeaking) {
      isAvatarSpeakingRef.current = false;
      currentTaskIdRef.current = null;
      setIsProcessing(false);
      setIsListening(true);
    }

    // Clear all pending timeouts
    if (speechEndTimeoutRef.current) {
      clearTimeout(speechEndTimeoutRef.current);
      speechEndTimeoutRef.current = null;
    }
    if (audioBufferTimeoutRef.current) {
      clearTimeout(audioBufferTimeoutRef.current);
      audioBufferTimeoutRef.current = null;
    }

    // Unmute USER's microphone - avatar finished speaking
    unmuteUserMic(reason);
  };

  // Schedule speech end timeout - if no messages for 2 seconds, assume speech ended
  const scheduleSpeechEndTimeout = () => {
    // Clear existing timeout
    if (speechEndTimeoutRef.current) {
      clearTimeout(speechEndTimeoutRef.current);
    }

    speechEndTimeoutRef.current = setTimeout(() => {
      const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;
      if (timeSinceLastMessage >= 1800 && isAvatarSpeakingRef.current) {
        console.log('[Avatar:HeyGen] ⏱️ Speech timeout - no messages for 2s, assuming speech ended');
        handleSpeechEnd('timeout');

        // Evaluate accumulated utterance
        const trimmedUtterance = coachUtteranceRef.current.trim();
        if (trimmedUtterance && trimmedUtterance.length > 20) {
          evaluateStageProgress(trimmedUtterance);
          coachUtteranceRef.current = '';
        }
      }
    }, 2000);
  };

  const evaluateStageProgress = (message: string) => {
    if (!message || message.trim().length === 0) {
      console.log('[Avatar:Stage] ⚠️ Empty message, skipping evaluation');
      return;
    }

    if (!companyConfig.stages) return;

    // Normalize message: remove extra whitespace and convert to lowercase
    const normalizedMessage = message.replace(/\s+/g, ' ').trim().toLowerCase();

    console.log('[Avatar:Stage] 📊 Evaluating stage transition:', {
      current: currentStage,
      currentStage: stageNames[currentStage],
      messageLength: message.length,
      messageStart: normalizedMessage.substring(0, 50),
      messageEnd: normalizedMessage.substring(normalizedMessage.length - 50)
    });

    // Company-specific stage detection patterns
    if (companyConfig.id === 'grab') {
      // Define stage patterns for Grab training
      const stagePatterns = {
        1: { // GrabCar
          keywords: ['grabcar', 'grab car'],
          phrases: ["let's begin with grabcar", 'begin with grabcar', 'private hire car service'],
        },
        2: { // GrabTaxi
          keywords: ['grabtaxi', 'grab taxi'],
          phrases: ['next up, grabtaxi', 'move on to grabtaxi', 'licensed taxi drivers'],
        },
        3: { // GrabFood/GrabExpress
          keywords: ['grabfood', 'grab food', 'grabexpress', 'grab express'],
          phrases: ["let's talk about grabfood", 'now lets talk', 'move on to grabfood', 'grabfood delivers meals'],
        },
        4: { // Quiz
          keywords: ['pop quiz', 'alright, pop quiz', 'alright pop quiz'],
          phrases: ['which modes of delivery', 'test your recall'],
        },
      };

      // Check each stage in order
      for (let stage = 1; stage <= 4; stage++) {
        // Only check if we haven't reached this stage yet
        if (currentStage < stage) {
          const pattern = stagePatterns[stage];
          const keywordMatch = pattern.keywords.some(kw => normalizedMessage.includes(kw));
          const phraseMatch = pattern.phrases.some(phrase => normalizedMessage.includes(phrase));

          if (keywordMatch || phraseMatch) {
            console.log(`[Avatar:Stage] ✅ Stage ${stage} detected:`, {
              stage: stageNames[stage],
              keywordMatch,
              phraseMatch,
              matchedIn: normalizedMessage.substring(0, 100),
            });
            advanceStage(stage, `${stageNames[stage]} detected in message`);
            return; // Exit after first match
          }
        }
      }

      console.log('[Avatar:Stage] ℹ️ No stage transition detected in message');
    } else if (companyConfig.id === 'uob') {
      // UOB Financial Consultant Training stage patterns
      // Triggers that indicate the START of each stage
      const stageTriggers: Record<number, { keywords: string[]; phrases: string[] }> = {
        1: { // Trigger for Core Principles
          keywords: ['four core principles', 'core principles', 'first principle'],
          phrases: ["let's begin with the four core principles", "ethical backbone", "begin with the four", "customers' interest first"],
        },
        2: { // Trigger for Exempt & Excluded Persons
          keywords: ['exempt persons', 'excluded persons', 'exempt person', 'excluded person'],
          phrases: ["that's exempt persons", "licensed banks and insurance", "entities that don't need", "moving on to exempt", "now let's talk about exempt"],
        },
        3: { // Trigger for Knowledge Check / Quiz
          keywords: ['pop quiz', 'pop-up quiz', 'knowledge check', 'test your understanding'],
          phrases: ["alright! pop-up quiz", "which principle ensures", "let's test", "time for a quiz"],
        },
        4: { // Trigger for Conclusion
          keywords: ['next module', 'goodbye', 'bye', 'completed'],
          phrases: ["types of financial advisory services", "see you in the next", "we will cover", "that concludes", "well done"],
        }
      };

      let stageTransitioned = false;

      // Check if we should advance to the next stage
      const nextStage = currentStageRef.current + 1;
      if (nextStage <= 4) {
        const pattern = stageTriggers[nextStage];
        if (pattern) {
          const keywordMatch = pattern.keywords.some(kw => normalizedMessage.includes(kw));
          const phraseMatch = pattern.phrases.some(phrase => normalizedMessage.includes(phrase));

          if (keywordMatch || phraseMatch) {
            console.log(`[Avatar:Stage] ✅ UOB Transition to Stage ${nextStage} detected:`, {
              stage: stageNames[nextStage],
              keywordMatch,
              phraseMatch,
              matchedIn: normalizedMessage.substring(0, 100),
            });
            advanceStage(nextStage, `${stageNames[nextStage]} detected in message`);
            stageTransitioned = true;
          }
        }
      }

      // Special handling for Exempt & Excluded stage (stage 2)
      if (currentStageRef.current === 2) {
        // Script step 3: Just exempt persons
        const exemptOnlyPhrases = [
          "that's exempt persons under the faa",
          "exempt persons under the faa. examples include",
          "licensed banks and insurance companies",
          "already regulated under other acts"
        ];

        // Script step 3.1: Comparison explanation
        const comparisonPhrases = [
          "let's break it down",
          "great question! let's break",
          "exempt persons are organisations",
          "excluded persons, on the other hand"
        ];

        if (comparisonPhrases.some(phrase => normalizedMessage.includes(phrase))) {
          if (exemptMode !== 'comparison') {
            setExemptMode('comparison');
            console.log('[Avatar:Stage] 📊 Showing Exempt vs Excluded comparison');
          }
        } else if (exemptOnlyPhrases.some(phrase => normalizedMessage.includes(phrase))) {
          if (exemptMode !== 'exempt-only') {
            setExemptMode('exempt-only');
            console.log('[Avatar:Stage] 📋 Showing Exempt Persons only');
          }
        }
      } else if (exemptMode !== null) {
        setExemptMode(null);
      }

      // Special handling for Quiz stage (stage 3)
      if (currentStageRef.current === 3) {
        // Question transitions - detect Q2 and reset state atomically
        if (normalizedMessage.includes('question 2') || normalizedMessage.includes('second question')) {
          // Only transition if we're not already on Q2
          if (quizQuestion !== 2) {
            console.log('[Avatar:Quiz] Transitioning to Question 2 - resetting state');
            setQuizQuestion(2);
            setQuizState('waiting');
            setUserQuizAnswer(null);
            // Clear the utterance buffer to prevent Q1 reveal phrases from affecting Q2
            coachUtteranceRef.current = '';
            return; // Exit early to prevent further evaluation with stale data
          }
        }

        // Question-specific reveal detection to prevent cross-question contamination
        // Q1: "Which principle ensures... cannot represent more than one principal"
        // Q1 answer: "Correct! That's the principle of Accountability"
        const q1RevealPhrases = [
          "that's the principle of accountability",
          "correct! that's the principle"
        ];

        // Q2: "Which of the following is considered an exempt person"
        // Q2 answer: "The correct answer is B—a licensed bank"
        const q2RevealPhrases = [
          "correct answer is b",
          "a licensed bank is an exempt person"
        ];

        // Only check reveal phrases for the CURRENT question
        const currentRevealPhrases = quizQuestion === 1 ? q1RevealPhrases : q2RevealPhrases;

        if (currentRevealPhrases.some(phrase => normalizedMessage.includes(phrase))) {
          if (quizState !== 'revealed') {
            setQuizState('revealed');
            console.log(`[Avatar:Quiz] Q${quizQuestion} answer revealed by coach`);
          }
        }
      } else if (quizState !== 'waiting') {
        // Reset quiz state when not in quiz stage
        setQuizState('waiting');
        setUserQuizAnswer(null);
      }

      // Special handling for Core Principles sub-stages - highlight active principle
      // Based on the FIXED UOB script flow
      if (currentStageRef.current === 1) {
        let detectedPrinciple: string | null = null;
        let isListingAllPrinciples = false;

        // PRIORITY 1: Check if we're in the "listing all 4 principles" phase (Step 2)
        // This takes precedence - show all 4 without individual highlight
        const allPrinciplesListPhrases = [
          "four core principles that underpin",
          "let's begin with the four core principles",
          "these are the ethical backbone",
          "b – consistency",  // When listing all 4 together
          "c – accountability",  // When listing all 4 together
          "d – independence",  // When listing all 4 together
        ];

        // Check if this contains the "all principles" listing
        // AND does NOT contain specific explanation phrases
        const hasAllPrinciplesList = allPrinciplesListPhrases.some(phrase => normalizedMessage.includes(phrase));

        // Specific explanation trigger phrases (only appear in detailed explanations)
        const consistencyExplanationTrigger = "absolutely ! ' consistency ' means";
        const independenceExplanationTrigger = "a firm can only call itself independent";

        const hasDetailedExplanation =
          normalizedMessage.includes(consistencyExplanationTrigger) ||
          normalizedMessage.includes(independenceExplanationTrigger);

        if (hasAllPrinciplesList && !hasDetailedExplanation) {
          // We're in the "listing all 4" phase - show all, no highlight
          isListingAllPrinciples = true;
          detectedPrinciple = 'ALL';  // Special value meaning show all 4
          console.log('[Avatar:Stage] 📋 Listing all 4 principles - showing all');
        } else {
          // PRIORITY 2: Check for specific principle detailed explanations
          // These only trigger when we have specific explanation phrases

          // Step 2.1 - Explaining Consistency (B) in detail
          const consistencyPhrases = [
            "absolutely ! ' consistency ' means",
            "'consistency' means that the same rules",
            "consistency means that the same rules",
            "same rules apply across different channels",
            "both advisers must follow the same regulatory standards"
          ];

          // Step 2.2/2.3 - Explaining Independence (D) in detail
          const independencePhrases = [
            "a firm can only call itself independent",
            "receiving no commissions from product providers",
            "structural independence is very difficult",
            "true structural independence",
            "'independence' principle is less about",
            "free from the conflicts of interest"
          ];

          if (consistencyPhrases.some(phrase => normalizedMessage.includes(phrase))) {
            detectedPrinciple = 'B';
            console.log('[Avatar:Stage] 🎯 Script match: Consistency (B) detailed explanation');
          } else if (independencePhrases.some(phrase => normalizedMessage.includes(phrase))) {
            detectedPrinciple = 'D';
            console.log('[Avatar:Stage] 🎯 Script match: Independence (D) detailed explanation');
          }
        }

        // Check for summary phrase to prepare for next stage
        const summaryPhrases = [
          "these principles guide how you interact",
          "guide how you interact with clients",
          "that covers all four",
          "those are the four principles"
        ];
        const hasSummary = summaryPhrases.some(phrase => normalizedMessage.includes(phrase));

        if (hasSummary && !isListingAllPrinciples) {
          // Only clear if we're not in the listing phase (summary comes at end of listing)
          setActivePrinciple(null);
          lastDetectedPrincipleRef.current = null;
          console.log('[Avatar:Stage] 📋 Principles summary - clearing highlight');
        } else if (detectedPrinciple !== undefined && detectedPrinciple !== lastDetectedPrincipleRef.current) {
          setActivePrinciple(detectedPrinciple);
          lastDetectedPrincipleRef.current = detectedPrinciple;
          if (detectedPrinciple && detectedPrinciple !== 'ALL') {
            console.log(`[Avatar:Stage] 🔍 Active principle: ${detectedPrinciple}`);
          }
        }
      } else {
        // Clear active principle when not in Core Principles stage
        if (activePrinciple !== null) {
          setActivePrinciple(null);
          lastDetectedPrincipleRef.current = null;
        }
      }

      if (!stageTransitioned) {
        console.log('[Avatar:Stage] ℹ️ No stage transition detected in message');
      }
    } else if (companyConfig.id === 'boi2') {
      // BOI2 Wealth Management CX Demo stage transitions
      const boi2Patterns = [
        { stage: 1, keywords: ['investing is part of it', 'bigger picture', 'ongoing relationship', 'grow your wealth'] },
        { stage: 2, keywords: ['central bank of ireland', 'regulated', 'client protection'] },
        { stage: 3, keywords: ['fact find', 'risk assessment', 'written recommendation', 'conversation about your finances'] },
        { stage: 4, keywords: ['risk profiling', 'time horizon', 'market fluctuations', 'minimum', 'depend on the type'] },
        { stage: 5, keywords: ['retirement planning', 'pension', 'prsas', 'arfs'] },
      ];

      for (const pattern of boi2Patterns) {
        if (currentStage < pattern.stage) {
          const match = pattern.keywords.some(kw => normalizedMessage.includes(kw));
          if (match) {
            console.log(`[Avatar:Stage] ✅ BOI2 Stage ${pattern.stage} detected`);
            advanceStage(pattern.stage, `${stageNames[pattern.stage]} detected`);
            return;
          }
        }
      }
    } else {
      // BOI-specific stage transitions
      const boiPatterns = [
        { stage: 1, keywords: ['where you are in life', 'financial goal'] },
        { stage: 2, keywords: ['risk', 'comfort matters'] },
        { stage: 3, keywords: ['growth builder', 'compare with others'] },
        { stage: 4, keywords: ['portfolio', 'etf'] },
        { stage: 5, keywords: ['account setup', 'kyc', 'verify your id'] },
      ];

      for (const pattern of boiPatterns) {
        if (currentStage < pattern.stage) {
          const match = pattern.keywords.some(kw => normalizedMessage.includes(kw));
          if (match) {
            console.log(`[Avatar:Stage] ✅ BOI Stage ${pattern.stage} detected`);
            advanceStage(pattern.stage, `${stageNames[pattern.stage]} detected`);
            return;
          }
        }
      }
    }
  };

  // Initialize HeyGen avatar
  const initializeAvatar = async () => {
    try {
      setIsProcessing(true);

      let token = heyGenToken;
      if (!token) {
        console.log('Generating HeyGen token...');
        token = await generateToken();
      }

      if (!token) {
        throw new Error('Failed to get HeyGen token');
      }

      console.log('[Avatar:HeyGen] Initializing avatar:', companyConfig.avatar.avatarId);
      const avatar = new StreamingAvatar({ token });
      avatarRef.current = avatar;

      // Set up event listeners
      avatar.on(StreamingEvents.AVATAR_START_TALKING, (e: any) => {
        const taskId = e?.detail?.task_id;
        debugLog('🎙️ AVATAR_START_TALKING', {
          taskId,
          alreadySpeaking: isAvatarSpeakingRef.current,
          currentTask: currentTaskIdRef.current,
          timeSinceLastMsg: lastMessageTimeRef.current > 0 ? `${Date.now() - lastMessageTimeRef.current}ms` : 'N/A',
          detail: e?.detail,
        });

        // If already speaking (handled via fallback), skip
        if (isAvatarSpeakingRef.current) {
          debugLog('ℹ️ Already speaking - skipping START_TALKING');
          return;
        }

        const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;

        // Ignore stale START_TALKING events (fired after speech already ended)
        if (lastMessageTimeRef.current > 0 && timeSinceLastMessage > 3000) {
          debugLog('⚠️ Ignoring stale AVATAR_START_TALKING', {
            timeSinceLastMessage: Math.round(timeSinceLastMessage / 1000) + 's'
          });
          return;
        }

        isAvatarSpeakingRef.current = true;
        if (taskId) currentTaskIdRef.current = taskId;
        updateActivity();
        setIsProcessing(true);
        setIsListening(false);

        // Cancel any pending speech end timeout
        if (speechEndTimeoutRef.current) {
          clearTimeout(speechEndTimeoutRef.current);
          speechEndTimeoutRef.current = null;
        }

        // Mute USER's microphone to prevent interruptions while avatar is speaking
        muteUserMic('avatar started talking');
      });

      // AVATAR_STOP_TALKING - primary signal for when audio playback completes
      avatar.on(StreamingEvents.AVATAR_STOP_TALKING, (e: any) => {
        const taskId = e?.detail?.task_id;
        const durationMs = e?.detail?.duration_ms;
        debugLog('🛑 AVATAR_STOP_TALKING', {
          taskId,
          durationMs,
          currentTask: currentTaskIdRef.current,
          isSpeaking: isAvatarSpeakingRef.current,
          hasAudioBufferTimeout: !!audioBufferTimeoutRef.current,
        });
        updateActivity();

        if (isAvatarSpeakingRef.current) {
          // Add a small delay before unmuting to avoid speaker feedback triggering USER_START
          if (audioBufferTimeoutRef.current) {
            clearTimeout(audioBufferTimeoutRef.current);
          }
          audioBufferTimeoutRef.current = setTimeout(() => {
            debugLog('⏱️ Post-STOP buffer complete - now unmuting');
            handleSpeechEnd('STOP_TALKING + buffer', taskId);

            // Evaluate accumulated utterance
            const trimmedUtterance = coachUtteranceRef.current.trim();
            if (trimmedUtterance && trimmedUtterance.length > 20) {
              evaluateStageProgress(trimmedUtterance);
              coachUtteranceRef.current = '';
            }
          }, 300); // 300ms buffer to let speaker audio fade out
        }
      });

      avatar.on(StreamingEvents.STREAM_READY, (event: any) => {
        debugLog('📺 STREAM_READY', { detail: event?.detail });
        setIsInitializing(false);
        setIsProcessing(false);

        setTimeout(() => {
          if (videoContainerRef.current && event.detail) {
            console.log('[Avatar:HeyGen] Appending video element');

            let videoElement;
            if (event.detail.htmlElement) {
              videoElement = event.detail.htmlElement;
            } else if (event.detail instanceof MediaStream) {
              videoElement = document.createElement('video');
              videoElement.srcObject = event.detail;
              videoElement.autoplay = true;
              videoElement.playsInline = true;
              videoElement.style.width = '100%';
              videoElement.style.height = '100%';
              videoElement.style.objectFit = 'cover';
            }

            if (videoElement) {
              avatarVideoElementRef.current = videoElement;
              videoContainerRef.current.innerHTML = '';
              videoContainerRef.current.appendChild(videoElement);

              videoElement.onloadedmetadata = () => {
                videoElement.play()
                  .then(() => console.log('[Avatar:HeyGen] Video playing'))
                  .catch((error) => console.error('[Avatar:HeyGen] Error playing video:', error));
              };
            }
          }
        }, 200);
      });

      avatar.on(StreamingEvents.USER_START, (e: any) => {
        debugLog('🎧 USER_START', {
          isSpeaking: isAvatarSpeakingRef.current,
          currentTask: currentTaskIdRef.current,
          detail: e?.detail,
        });
        userTranscriptionReceivedRef.current = false; // Reset transcription flag
        updateActivity();
        if (!isAvatarSpeakingRef.current) {
          setIsListening(true);
        }
      });

      avatar.on(StreamingEvents.USER_STOP, (e: any) => {
        debugLog('🛑 USER_STOP', {
          isSpeaking: isAvatarSpeakingRef.current,
          hadTranscription: userTranscriptionReceivedRef.current,
          currentTask: currentTaskIdRef.current,
          detail: e?.detail,
        });
        updateActivity();

        // Update UI state but NO automatic muting
        if (userTranscriptionReceivedRef.current && !isAvatarSpeakingRef.current) {
          setIsListening(false);
          setIsProcessing(true);
          debugLog('📊 USER_STOP with transcription - expecting AI response (NO auto mute)');
        } else if (!userTranscriptionReceivedRef.current) {
          debugLog('📊 USER_STOP without transcription - user can retry');
          setIsListening(true);
        }
      });

      avatar.on(StreamingEvents.USER_TALKING_MESSAGE, (event: any) => {
        const message = event?.detail?.message;
        if (message) {
          debugLog('🗣️ USER_TALKING_MESSAGE', {
            message,
            isSpeaking: isAvatarSpeakingRef.current,
            currentTask: currentTaskIdRef.current,
          });
          userTranscriptionReceivedRef.current = true; // Mark that we got a transcription
          updateActivity();

          // Update UI state but NO automatic muting
          if (!isAvatarSpeakingRef.current) {
            setIsListening(false);
            setIsProcessing(true);
            debugLog('📊 Got user transcription - AI will respond (NO auto mute)');
          }
        }
      });

      avatar.on(StreamingEvents.AVATAR_TALKING_MESSAGE, (event: any) => {
        const message = typeof event?.detail?.message === 'string' ? event.detail.message : undefined;
        const taskId = event?.detail?.task_id;
        if (!message) return;

        // Only log occasionally to reduce noise (every 5th message or first message)
        const msgCount = coachUtteranceRef.current.split(' ').length;
        if (msgCount % 5 === 0 || msgCount === 0) {
          debugLog('🧠 AVATAR_TALKING_MESSAGE', {
            message: message.substring(0, 30),
            taskId,
            msgCount,
            isSpeaking: isAvatarSpeakingRef.current,
          });
        }
        updateActivity();

        // Track task_id for correlating events
        if (taskId && !currentTaskIdRef.current) {
          currentTaskIdRef.current = taskId;
          debugLog('📋 Started tracking task', { taskId });
        }

        // Track when we received this message for stale event detection
        lastMessageTimeRef.current = Date.now();

        coachUtteranceRef.current = `${coachUtteranceRef.current} ${message}`.trim();

        // FALLBACK: If AVATAR_START_TALKING didn't fire, mute user's mic now
        if (!isAvatarSpeakingRef.current) {
          debugLog('⚠️ AVATAR_TALKING_MESSAGE without START_TALKING - muting user mic');
          isAvatarSpeakingRef.current = true;
          setIsProcessing(true);
          setIsListening(false);
          muteUserMic('avatar talking (fallback)');
        }

        // Clear any pending audio buffer timeout (we're still receiving messages)
        if (audioBufferTimeoutRef.current) {
          clearTimeout(audioBufferTimeoutRef.current);
          audioBufferTimeoutRef.current = null;
        }

        // Schedule speech end timeout as fallback - resets with each message
        // If no messages for 2 seconds, we assume speech has ended
        scheduleSpeechEndTimeout();

        // Real-time stage evaluation
        evaluateStageProgress(coachUtteranceRef.current);
      });

      // DEBUG: AVATAR_END_MESSAGE - observe timing
      avatar.on(StreamingEvents.AVATAR_END_MESSAGE, (event: any) => {
        const taskId = event?.detail?.task_id;
        debugLog('📨 AVATAR_END_MESSAGE', {
          taskId,
          currentTask: currentTaskIdRef.current,
          isSpeaking: isAvatarSpeakingRef.current,
          detail: event?.detail,
        });
        updateActivity();

        // Clear the fallback timeout - we have a signal
        if (speechEndTimeoutRef.current) {
          clearTimeout(speechEndTimeoutRef.current);
          speechEndTimeoutRef.current = null;
        }

        // DON'T immediately end speech - wait for AVATAR_STOP_TALKING
        // Just log for debugging
        debugLog('📨 END_MESSAGE received - waiting for STOP_TALKING to confirm audio complete');
      });

      avatar.on(StreamingEvents.STREAM_DISCONNECTED, () => {
        debugLog('❌ STREAM_DISCONNECTED');
        stopKeepAlive();
        if (speechEndTimeoutRef.current) {
          clearTimeout(speechEndTimeoutRef.current);
          speechEndTimeoutRef.current = null;
        }
        if (audioBufferTimeoutRef.current) {
          clearTimeout(audioBufferTimeoutRef.current);
          audioBufferTimeoutRef.current = null;
        }
        voiceChatReadyRef.current = false;
        isAvatarSpeakingRef.current = false;
        currentTaskIdRef.current = null;

        if (!isLeavingRef.current) {
          setError('Connection lost. Please refresh to try again.');
        }
      });

      // Create avatar session
      // Use voice-only voiceId if in voice mode and it's available, otherwise use default
      const voiceId = mode === 'voice' && companyConfig.avatar.voiceIdVoiceOnly
        ? companyConfig.avatar.voiceIdVoiceOnly
        : companyConfig.avatar.voiceId;

      const sessionConfig = {
        avatarName: companyConfig.avatar.avatarId,
        quality: AvatarQuality.Medium, // Upgraded from Low to Medium for better stability in v2.1.0
        knowledgeBase: companyConfig.systemPrompt,
        voice: {
          voiceId: voiceId,
        },
        language: companyConfig.language || 'en-US',
        activityIdleTimeout: 600, // v2.1.0: Use activityIdleTimeout instead of disableIdleTimeout
        useAvatarBackground: false,
      };

      console.log('[Avatar:HeyGen] Session config:', {
        mode,
        voiceId,
        avatarId: companyConfig.avatar.avatarId,
      });

      console.log('[Avatar:HeyGen] Starting avatar session...');
      const sessionData = await avatar.createStartAvatar(sessionConfig);
      console.log('[Avatar:HeyGen] Avatar session created:', sessionData);

      // Start voice chat immediately - AI will speak first based on system prompt/instructions
      // Auto-muting when agent speaks prevents echo
      startVoiceChat().catch((error) => {
        console.error('Failed to start voice chat:', error);
      });
    } catch (error: any) {
      console.error('Failed to initialize avatar:', error);
      // HeyGen SDK throws APIError with status and body text
      if (error?.status) {
        console.error('[Avatar:HeyGen] API error status:', error.status, 'body:', error.body || error.message);
      }
      setError('Failed to initialize assistant. Please refresh and try again.');
      setIsProcessing(false);
      setIsInitializing(false);
    }
  };

  // Start call
  const handleStartCall = async () => {
    try {
      setIsCallActive(true);
      setSessionStartTime(Date.now());

      await initializeUserMedia();
      await initializeAvatar();
    } catch (error) {
      console.error('Failed to start call:', error);
      setIsInitializing(false);
    }
  };

  // End call
  const handleEndCall = async () => {
    isLeavingRef.current = true;
    stopKeepAlive();
    setIsCallActive(false);
    voiceChatReadyRef.current = false;

    if (avatarRef.current) {
      try {
        await avatarRef.current.stopAvatar();
      } catch (error) {
        console.error('Error stopping avatar:', error);
      }
      avatarRef.current = null;
    }

    if (userStream) {
      userStream.getTracks().forEach((track) => track.stop());
    }

    if (deviceId) {
      const sessionId = `${companyConfig.id}-session-${Date.now()}`;
      await endSession({
        sessionId,
        moduleId: `${companyConfig.id}-training`,
        sessionType: mode,
        deviceId,
        transcriptData: {
          messages: [
            {
              role: 'coach' as const,
              content: `Training session completed. Stage reached: ${stageNames[currentStage]}`,
              timestamp: new Date().toISOString(),
            },
          ],
        },
      });
    }

    setTimeout(() => {
      navigate(`/avatar?company=${companyConfig.id}`);
    }, 2000);
  };

  // Toggle USER's microphone (NOT avatar's audio)
  const toggleAudio = () => {
    if (userStream) {
      const audioTrack = userStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        const audioEnabled = audioTrack.enabled;
        setIsAudioOn(audioEnabled);
        debugLog(`🎙️ USER MIC TOGGLED: ${audioEnabled ? 'ON' : 'OFF'}`);
        if (audioEnabled) {
          ensureUserMicrophoneActive();
        }
        if (voiceChatReadyRef.current && avatarRef.current) {
          try {
            if (audioEnabled) {
              avatarRef.current.unmuteInputAudio();
            } else {
              avatarRef.current.muteInputAudio();
            }
          } catch (error) {
            console.error('[Avatar:HeyGen] Failed to toggle user microphone:', error);
          }
        }
      }
    }
  };

  // Handle quiz answer selection - highlights the selection and prompts verbal confirmation
  const handleQuizAnswer = async (answer: string) => {
    if (quizState !== 'waiting') return;

    setUserQuizAnswer(answer);
    // Keep in 'waiting' state until AI reveals the answer
    // The visual shows as 'selected' via userQuizAnswer

    console.log(`[Avatar:Quiz] User selected answer: ${answer}`);
    console.log('[Avatar:Quiz] User should now verbally confirm their answer to the AI');

    // Ensure user's mic is unmuted so they can speak their answer
    if (avatarRef.current && voiceChatReadyRef.current) {
      try {
        avatarRef.current.unmuteInputAudio();
        debugLog('🎤 Mic unmuted for user to say answer');
      } catch (e) {
        console.warn('[Avatar:Quiz] Failed to unmute:', e);
      }
    }
  };

  // Toggle AVATAR's audio output (speaker)
  const toggleSpeaker = () => {
    if (avatarVideoElementRef.current) {
      const newMutedState = isSpeakerOn; // If speaker is on, we're muting it
      avatarVideoElementRef.current.muted = newMutedState;
      setIsSpeakerOn(!isSpeakerOn);
      debugLog(`🔊 AVATAR AUDIO TOGGLED: ${!newMutedState ? 'ON' : 'OFF'}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/avatar?company=${companyConfig.id}`)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {isZh ? '返回' : 'Back'}
            </Button>
            <div className="flex items-center gap-3">
              <Building2
                className="w-5 h-5"
                style={{ color: companyConfig.colors.primary }}
              />
              <h1 className="text-lg font-semibold">
                {companyConfig.name} - {companyConfig.avatar.coachName}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {isCallActive && !isInitializing && !isCxScenario && (
              <div className="text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                {stageNames[currentStage]}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Powered by</span>
              <img
                src="/logos/Hupo_Logotype_Orange(noR).svg"
                alt="Hupo"
                className="h-5"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4">
        {/* Loading State */}
        {isInitializing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center min-h-[600px]"
          >
            <Loader2
              className="w-12 h-12 animate-spin mb-4"
              style={{ color: companyConfig.colors.primary }}
            />
            <h3 className="text-xl font-semibold mb-2">
              {isZh ? `正在连接 ${companyConfig.avatar.coachName}...` : `Connecting to ${companyConfig.avatar.coachName}...`}
            </h3>
            <p className="text-slate-600">
              {isZh
                ? `正在准备${mode === 'video' ? '视频' : '语音'}会话`
                : `Setting up your ${mode === 'video' ? 'video' : 'voice'} session`}
            </p>
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto mt-20 bg-red-50 border border-red-200 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              {isZh ? '连接错误' : 'Connection Error'}
            </h3>
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              {isZh ? '刷新页面' : 'Refresh Page'}
            </Button>
          </motion.div>
        )}

        {/* Active Call */}
        {isCallActive && !isInitializing && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={cn("grid gap-4", (companyConfig.id === 'boi2' || isCxScenario) ? 'lg:grid-cols-1' : 'lg:grid-cols-3')}
          >
            {/* Main video area */}
            <div className={cn("space-y-4", !(companyConfig.id === 'boi2' || isCxScenario) && 'lg:col-span-2')}>
              <div className="relative bg-slate-900 rounded-xl overflow-hidden aspect-video shadow-2xl">
                {/* Video container */}
                <div ref={videoContainerRef} className="w-full h-full" />

                {/* UOB Visuals Overlay */}
                {companyConfig.id === 'uob' && (
                  <UOBVisuals
                    currentStage={currentStage}
                    quizQuestion={quizQuestion}
                    activePrinciple={activePrinciple}
                    exemptMode={exemptMode}
                    quizState={quizState}
                    userQuizAnswer={userQuizAnswer}
                    onQuizAnswer={handleQuizAnswer}
                  />
                )}

                {/* Avatar name overlay - hide when UOB visuals are showing */}
                {!(companyConfig.id === 'uob' && (
                  (currentStage === 1 && activePrinciple) ||
                  (currentStage === 2 && exemptMode) ||
                  currentStage === 3
                )) && (
                  <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 z-20">
                    <p className="text-white text-sm font-medium">
                      {companyConfig.avatar.coachName}
                    </p>
                    <p className="text-white/70 text-xs">
                      {companyConfig.id === 'boi' ? 'Financial Coach' : isZh ? '培训教练' : 'Training Coach'}
                    </p>
                  </div>
                )}

                {/* Processing indicator */}
                {isProcessing && (
                  <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-white text-sm">{isZh ? '正在说话...' : 'Speaking...'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Control bar */}
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center justify-between px-4">
                  {/* Status Indicator */}
                  <div className="flex items-center gap-3 w-1/3">
                    <div className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300",
                      isListening
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : isProcessing
                          ? "bg-blue-100 text-blue-700 border border-blue-200"
                          : "bg-slate-100 text-slate-500 border border-slate-200"
                    )}>
                      {isListening ? (
                        <>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                          </span>
                          {isZh ? '正在聆听' : 'Listening'}
                        </>
                      ) : isProcessing ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          {isZh ? '正在说话' : 'Speaking'}
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-slate-400" />
                          {isZh ? '连接中...' : 'Connecting...'}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Center Controls */}
                  <div className="flex items-center justify-center gap-4 w-1/3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={toggleAudio}
                      className={cn(
                        "rounded-full w-12 h-12 transition-all duration-200",
                        !isAudioOn && "bg-red-50 text-red-500 border-red-200 hover:bg-red-100 hover:text-red-600"
                      )}
                    >
                      {isAudioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                    </Button>

                    <Button
                      variant="destructive"
                      size="lg"
                      onClick={handleEndCall}
                      className="rounded-full px-8 h-12 gap-2 shadow-md hover:shadow-lg transition-all"
                    >
                      <PhoneOff className="w-5 h-5" />
                      <span className="hidden sm:inline">{isZh ? '结束通话' : 'End Call'}</span>
                    </Button>
                  </div>

                  {/* Right Spacer */}
                  <div className="w-1/3 flex justify-end">
                     <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleSpeaker}
                        className="rounded-full text-slate-500 hover:text-slate-700"
                      >
                        {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                      </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Side panel - Progress tracker */}
            {companyConfig.id !== 'boi2' && !isCxScenario && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm border p-6 h-full">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-6">Session Progress</h3>
                <div className="space-y-0 relative">
                  {/* Vertical line connecting steps */}
                  <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-slate-100" />

                  {stageNames.map((stage, idx) => {
                    const isCompleted = idx < currentStage;
                    const isCurrent = idx === currentStage;
                    const isUpcoming = idx > currentStage;

                    return (
                      <div
                        key={idx}
                        className={cn(
                          'relative flex gap-4 pb-8 last:pb-0 group',
                          isUpcoming && 'opacity-50',
                        )}
                      >
                        {/* Step Indicator */}
                        <div className="relative z-10 flex-shrink-0">
                          <motion.div
                            initial={false}
                            animate={{
                              backgroundColor: isCompleted ? '#22c55e' : isCurrent ? companyConfig.colors.primary : '#f1f5f9',
                              borderColor: isCurrent ? companyConfig.colors.primary : 'transparent',
                              scale: isCurrent ? 1.1 : 1,
                            }}
                            className={cn(
                              'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors duration-300',
                              isCompleted ? 'text-white' : isCurrent ? 'text-white shadow-lg' : 'text-slate-400'
                            )}
                          >
                            {isCompleted ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <span>{idx + 1}</span>
                            )}
                          </motion.div>

                          {/* Pulse effect for current step */}
                          {isCurrent && (
                            <div
                              className="absolute inset-0 rounded-full animate-ping opacity-20"
                              style={{ backgroundColor: companyConfig.colors.primary }}
                            />
                          )}
                        </div>

                        {/* Step Content */}
                        <div className={cn("pt-1 transition-all duration-300", isCurrent ? "translate-x-1" : "")}>
                          <p
                            className={cn(
                              "text-sm font-semibold leading-none mb-1 transition-colors",
                              isCurrent ? "text-slate-900" : "text-slate-600"
                            )}
                          >
                            {stage}
                          </p>
                          <p className="text-xs text-slate-500">
                            {isCompleted ? 'Completed' : isCurrent ? 'In Progress' : 'Upcoming'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Session Complete Modal */}
      {summaryData && !isCallActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">{isZh ? '会话已结束！' : 'Session Complete!'}</h3>
            <p className="text-slate-600 mb-4">
              {isZh
                ? '感谢您完成本次培训会话。'
                : `Thank you for completing your ${companyConfig.id === 'boi' ? 'financial coaching' : 'training'} session.`}
            </p>
            <Button
              onClick={() => navigate(`/avatar?company=${companyConfig.id}`)}
              className="w-full"
              style={{ background: companyConfig.colors.primary }}
            >
              {isZh ? '返回首页' : 'Back to Home'}
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
